import React, { useCallback, useState } from 'react';
import styled from '@emotion/native';
import { Subtitle, Title } from '../styledComponents';
import useExploreStore from '@/stores/useExploreStore';
import { format } from 'date-fns';
import Receipt from '@/shared/components/Receipt/Receipt';
import PaymentMethodDisplay from '@/shared/components/Receipt/PaymentMethodDisplay';
import PromoCodeDisplay from '@/shared/components/Receipt/PromoCodeDisplay';
import { ReceiptItemProps } from '@/shared/components/Receipt/ReceiptItem';
import { formatCurrency, getRateByTime } from '@/shared/utils';
import {
  DateAndTimeIcon,
  DurationIcon,
  ReasonIcon,
  StarIcon,
  StylistIcon,
  InPersonIcon,
  VirtualIcon,
} from '@/shared/components/Receipt/ReceiptIcons';
import { ReceiptLabelText } from '@/shared/components/Receipt/constants';
import { useRouter } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';
import SentryService from '@/services/sentry/Sentry.service';
import { useGetUser } from '@/shared/hooks/useGetUser';
import { formatAddress } from '@/shared/utils/sessionUtils';
import { getPreAuthNotice } from '../shared/preAuth';

interface Props {
  showHeader?: boolean;
  hideStylist?: boolean;
  priceTierName?: string;
  hourlyRate?: number;
  isInstant?: boolean;
  onDismissModal?: () => void;
  isInPersonSession?: boolean;
  onDiscountCodeChange?: (
    codeText: string | null,
    discountPercentage?: number | null,
  ) => void;
  /** A promo code already applied earlier in the flow */
  initialDiscountCode?: string | null;
  initialDiscountPercentage?: number | null;
  /** Show the applied promo code read-only */
  isPromoCodeLocked?: boolean;
}

const BookingSummary = ({
  showHeader,
  hideStylist,
  priceTierName,
  hourlyRate,
  isInstant,
  onDismissModal,
  isInPersonSession = false,
  onDiscountCodeChange,
  initialDiscountCode,
  initialDiscountPercentage,
  isPromoCodeLocked,
}: Props) => {
  const { booking, selectedStylist, setUserHasPaymentMethod } =
    useExploreStore();
  const { user } = useGetUser();
  const date = booking?.selectedDate;
  const time = booking?.selectedTime;
  const router = useRouter();
  const [discountPercentage, setDiscountPercentage] = useState<number | null>(
    initialDiscountPercentage ?? null,
  );

  // Get user's address for in-person sessions
  const userAddress = user?.address;
  const hasUserAddress = Boolean(userAddress?.streetAddress);

  const handlePaymentMethodStateChange = useCallback(
    (hasPaymentMethod: boolean) => {
      setUserHasPaymentMethod(hasPaymentMethod);
    },
    [setUserHasPaymentMethod],
  );

  const getPriceTierReceiptValue = () => {
    if (isInstant && hourlyRate) {
      return `${priceTierName} (${formatCurrency(hourlyRate)}/hr)`;
    }

    // In-person sessions use customInPersonHourlyRate
    if (isInPersonSession) {
      const inPersonRate =
        selectedStylist?.customInPersonHourlyRate ??
        selectedStylist?.pricing?.hourlyRate ??
        0;
      return `${selectedStylist?.pricing?.tierName} (${formatCurrency(inPersonRate)}/hr)`;
    }

    if (selectedStylist?.customHourlyRate) {
      return `${selectedStylist?.pricing?.tierName} (${formatCurrency(
        selectedStylist.customHourlyRate,
      )}/hr)`;
    }

    return `${selectedStylist?.pricing?.tierName} (${formatCurrency(
      selectedStylist?.pricing?.hourlyRate ?? 0,
    )}/hr)`;
  };

  const getDurationDisplayValue = () => {
    const duration = booking?.sessionDuration ?? 0;
    if (isInPersonSession && duration >= 60) {
      const hours = duration / 60;
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `${duration} mins`;
  };

  const receiptItems: ReceiptItemProps[] = [
    {
      label: ReceiptLabelText.SessionType,
      value: isInPersonSession ? `In-Person` : `Virtual`,
      icon: isInPersonSession ? <InPersonIcon /> : <VirtualIcon />,
    },
    // Add Session Location for in-person sessions
    ...(isInPersonSession && hasUserAddress
      ? [
          {
            label: ReceiptLabelText.SessionLocation,
            value: formatAddress(userAddress),
          } as ReceiptItemProps,
        ]
      : []),
    // Add Access Details for in-person sessions if available
    ...(isInPersonSession && userAddress?.accessDetails
      ? [
          {
            label: ReceiptLabelText.AccessDetails,
            value: userAddress.accessDetails,
          } as ReceiptItemProps,
        ]
      : []),
    {
      label: ReceiptLabelText.DateAndTime,
      value:
        date && time
          ? `${format(date, 'MM/dd/yy')} at ${format(time, 'h:mm a')}`
          : 'Missing date or time',
      icon: <DateAndTimeIcon />,
    },
    {
      label: ReceiptLabelText.Duration,
      value: getDurationDisplayValue(),
      icon: <DurationIcon />,
    },
    {
      label: ReceiptLabelText.Reason,
      value: booking?.reason,
      icon: <ReasonIcon />,
    },
    {
      label: ReceiptLabelText.Stylist,
      value: selectedStylist?.user?.firstName ?? 'Missing stylist',
      icon: <StylistIcon />,
    },
    ...(isInstant
      ? [
          {
            label: ReceiptLabelText.PriceTier,
            value: getPriceTierReceiptValue(),
            icon: <StarIcon />,
          },
        ]
      : []),
    {
      label: ReceiptLabelText.PromoCode,
      customElement: (
        <PromoCodeDisplay
          onDiscountApplied={(percentage, codeText) => {
            setDiscountPercentage(percentage);
            onDiscountCodeChange?.(codeText, percentage);
          }}
          onDiscountRemoved={() => {
            setDiscountPercentage(null);
            onDiscountCodeChange?.(null, null);
          }}
          // Instant sessions don't have a stylist until they're matched, so
          // only codes that work for every stylist can be applied
          stylistId={isInstant ? null : (selectedStylist?.id ?? null)}
          initialCodeText={initialDiscountCode ?? undefined}
          initialDiscountPercentage={initialDiscountPercentage ?? undefined}
          isReceiptItem={isPromoCodeLocked}
        />
      ),
    },
    {
      label: ReceiptLabelText.PaymentMethod,
      customElement: (
        <PaymentMethodDisplay
          onSuccess={() => {
            setUserHasPaymentMethod(true);
          }}
          onError={(error) => {
            SentryService.captureError(
              'Failed to update payment method',
              error,
            );
          }}
          onPaymentMethodStateChange={handlePaymentMethodStateChange}
        />
      ),
    },
  ]
    .filter((item) => !(hideStylist && item.label === 'Stylist'))
    // A locked promo row with no code applied would just be an empty box
    .filter(
      (item) =>
        !(
          isPromoCodeLocked &&
          !initialDiscountCode &&
          item.label === ReceiptLabelText.PromoCode
        ),
    );

  // Same rate BookingModal charges for scheduled sessions
  const getHourlyRate = () => {
    if (isInstant && hourlyRate) {
      return hourlyRate;
    }
    // In-person sessions use customInPersonHourlyRate
    if (isInPersonSession) {
      return (
        selectedStylist?.customInPersonHourlyRate ??
        selectedStylist?.pricing?.hourlyRate
      );
    }
    return (
      selectedStylist?.customHourlyRate ?? selectedStylist?.pricing?.hourlyRate
    );
  };

  // Locked summaries follow the parent's applied code, which can be removed
  // after a failed payment; editable ones track the promo field
  const appliedDiscountPercentage = isPromoCodeLocked
    ? (initialDiscountPercentage ?? null)
    : discountPercentage;

  // Same rounding as the backend's calculateDiscountAmounts
  const applyDiscount = (amount: number) => {
    if (appliedDiscountPercentage === null || appliedDiscountPercentage <= 0) {
      return amount;
    }
    return amount - Math.floor(amount * (appliedDiscountPercentage / 100));
  };

  const rate = getHourlyRate();

  const totalPrice = () => {
    if (!rate) {
      return 'Price Missing';
    }
    return formatCurrency(
      applyDiscount(getRateByTime(rate, booking.sessionDuration)),
    );
  };

  // Instant sessions keep the Receipt's default fine print
  const preAuthNotice =
    !isInstant && rate
      ? getPreAuthNotice(booking.sessionDuration, isInPersonSession)
      : undefined;

  // We do not want to show the help center if they're in the actual matching flow of instant styling
  const showHelpCenter = Boolean(onDismissModal);

  const onHelpCenterPress = () => {
    onDismissModal?.();
    router.push(ROUTES.HELP.path);
  };

  return (
    <Container>
      {showHeader && (
        <>
          <Title>Final touches</Title>
          <Subtitle
            style={{ marginBottom: 21, maxWidth: 250, textAlign: 'center' }}
          >
            Give your request a quick review before continuing.
          </Subtitle>
        </>
      )}
      <Receipt
        totalPrice={totalPrice()}
        items={receiptItems}
        notes={booking?.additionalDetails}
        finePrint={preAuthNotice}
        showHelpCenter={showHelpCenter}
        onHelpCenterPress={onHelpCenterPress}
      />
    </Container>
  );
};

const Container = styled.View({
  flex: 1,
  alignItems: 'center',
});

export default BookingSummary;
